import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Plus, Pencil, Trash2, Loader2, Quote } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const VAZIO = { name: "", phrases: "" };

const CampaignsPhrases = () => {
	const [listas, setListas] = useState([]);
	const [carregando, setCarregando] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selecionada, setSelecionada] = useState(null);
	const [dados, setDados] = useState(VAZIO);
	const [salvando, setSalvando] = useState(false);

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const { data } = await api.get("/campaign-phrases");
			setListas(data);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			if (selecionada) {
				await api.put(`/campaign-phrases/${selecionada.id}`, dados);
				toast.success("Lista alterada.");
			} else {
				await api.post("/campaign-phrases", dados);
				toast.success("Lista criada.");
			}
			setModalOpen(false);
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluir = async (id) => {
		try {
			await api.delete(`/campaign-phrases/${id}`);
			toast.success("Lista removida.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionada(null);
	};

	const quantidadeDigitada = dados.phrases
		.split("\n")
		.map((l) => l.trim())
		.filter(Boolean).length;

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionada && `Remover a lista "${selecionada.name}"?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionada(null);
				}}
				onConfirm={() => excluir(selecionada.id)}
			>
				As campanhas que usavam esta lista voltam a mandar o texto fixo que já
				têm gravado. Nenhuma campanha é apagada.
			</ConfirmationModal>

			<MainHeader>
				<Title>Listas de frases</Title>
				<MainHeaderButtonsWrapper>
					<Button
						onClick={() => {
							setSelecionada(null);
							setDados(VAZIO);
							setModalOpen(true);
						}}
					>
						<Plus className="h-4 w-4" />
						Nova lista
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				<p className="mb-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
					Várias formas de dizer a mesma coisa. Ao disparar, a campanha sorteia
					uma frase por contato — assim mil pessoas não recebem texto idêntico
					no mesmo minuto, que é justamente o padrão que o WhatsApp usa para
					identificar disparo em massa.
				</p>

				<div className="overflow-x-auto rounded-xl border bg-card">
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
								<TableHead>Nome</TableHead>
								<TableHead className="text-center">Variações</TableHead>
								<TableHead>Primeira frase</TableHead>
								<TableHead className="text-center">Ações</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{listas.map((lista) => (
								<TableRow key={lista.id}>
									<TableCell className="font-medium">
										<span className="flex items-center gap-2">
											<Quote className="h-4 w-4 text-muted-foreground" />
											{lista.name}
										</span>
									</TableCell>
									<TableCell className="text-center">
										<Badge variant="secondary">{lista.quantidade}</Badge>
									</TableCell>
									<TableCell className="max-w-md truncate text-muted-foreground">
										{String(lista.phrases || "").split("\n")[0]}
									</TableCell>
									<TableCell>
										<div className="flex items-center justify-center gap-1">
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8"
												aria-label="Editar lista"
												title="Editar lista"
												onClick={() => {
													setSelecionada(lista);
													setDados({
														name: lista.name,
														phrases: lista.phrases || "",
													});
													setModalOpen(true);
												}}
											>
												<Pencil className="h-4 w-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8 text-destructive hover:text-destructive"
												aria-label="Remover lista"
												title="Remover lista"
												onClick={() => {
													setSelecionada(lista);
													setConfirmOpen(true);
												}}
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
							{carregando && <TableRowSkeleton columns={4} />}
							{!carregando && listas.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={4}
										className="py-10 text-center text-sm text-muted-foreground"
									>
										Nenhuma lista ainda.
										<span className="mt-1 block text-xs">
											Depois de criar uma, escolha-a na campanha para ela
											variar o texto a cada contato.
										</span>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>

			<Dialog open={modalOpen} onOpenChange={setModalOpen}>
				<DialogContent className="sm:max-w-lg">
					<form onSubmit={salvar}>
						<DialogHeader>
							<DialogTitle>
								{selecionada ? "Editar lista" : "Nova lista"}
							</DialogTitle>
							<DialogDescription>
								Uma frase por linha. Todas devem dizer a mesma coisa — quem
								recebe não sabe que existem outras.
							</DialogDescription>
						</DialogHeader>

						<div className="grid gap-3 py-4">
							<div>
								<Label htmlFor="name">Nome da lista</Label>
								<Input
									id="name"
									required
									placeholder="Ex.: Aviso de promoção"
									value={dados.name}
									onChange={(e) =>
										setDados({ ...dados, name: e.target.value })
									}
								/>
							</div>
							<div>
								<Label htmlFor="phrases">Frases</Label>
								<Textarea
									id="phrases"
									required
									rows={8}
									placeholder={
										"Oi {{name}}, temos novidade para você!\nOlá {{name}}, apareceu uma novidade por aqui.\n{{name}}, dá uma olhada no que preparamos."
									}
									value={dados.phrases}
									onChange={(e) =>
										setDados({ ...dados, phrases: e.target.value })
									}
								/>
								<p className="mt-1 text-xs text-muted-foreground">
									{quantidadeDigitada} variação(ões). Vale usar{" "}
									<code className="rounded bg-muted px-1">{"{{name}}"}</code>{" "}
									como em qualquer campanha.
								</p>
							</div>
						</div>

						<DialogFooter>
							<Button
								type="button"
								variant="ghost"
								onClick={() => setModalOpen(false)}
							>
								Cancelar
							</Button>
							<Button type="submit" disabled={salvando}>
								{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
								Salvar
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</MainContainer>
	);
};

export default CampaignsPhrases;
